import React, { useCallback, useMemo } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { LeftOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment';

const CALENDAR_STATUS_LEGEND_CONFIG = [
	{ key: 'Scheduled', label: 'Scheduled' },
	{ key: 'PastDue', label: 'Scheduled (Past Due)' },
	{ key: 'CheckedIn', label: 'Checked In' },
	{ key: 'OnHold', label: 'On Hold/ Incomplete' },
	{ key: 'Completed', label: 'Completed' },
];

interface CustomCalendarToolbarProps {
	date: Date;
	view: string;
	views: string[];
	onNavigate: (action: string) => void;
	onView: (view: string) => void;
	leftExtra?: React.ReactNode;
	/** Schedule panel open: the legend moves to its own row instead of sharing the toolbar row. */
	compact?: boolean;
}

const CustomCalendarToolbar: React.FC<CustomCalendarToolbarProps> = ({
	date,
	view,
	views,
	onNavigate,
	onView,
	leftExtra,
	compact,
}) => {
	const handleToday = useCallback(() => {
		onNavigate('TODAY');
	}, [onNavigate]);

	const handlePrev = useCallback(() => {
		onNavigate('PREV');
	}, [onNavigate]);

	const handleNext = useCallback(() => {
		onNavigate('NEXT');
	}, [onNavigate]);

	const handleViewChange = useCallback(
		({ key }: { key: string }) => {
			onView(key as string);
		},
		[onView]
	);

	const currentViewLabel = useMemo(() => {
		if (view === 'week') {
			const weekStart = moment(date).startOf('week');
			const weekEnd = moment(date).endOf('week');
			return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
		}
		return moment(date).format('ddd, DD MMM YYYY');
	}, [view, date]);

	const viewMenu = useMemo(
		() => (
			<Menu onClick={handleViewChange}>
				{views.map((viewName) => (
					<Menu.Item key={viewName}>
						{viewName.charAt(0).toUpperCase() + viewName.slice(1)}
					</Menu.Item>
				))}
			</Menu>
		),
		[views, handleViewChange]
	);

	const myTimeZoneAbbreviation = moment.tz(new Date(), moment.tz.guess()).format('z');

	const legend = (
		<div
			className={`flex flex-row flex-wrap items-center justify-end gap-x-4 gap-y-1 ${
				compact ? '' : 'flex-1'
			}`}
		>
			{CALENDAR_STATUS_LEGEND_CONFIG.map((item) => (
				<span key={item.key} className="flex flex-row items-center gap-2">
					<span className={`calendarLegendSwatch calendarLegendSwatch--${item.key}`} />
					<span className="footnoteregular text-character-secondary">{item.label}</span>
				</span>
			))}
		</div>
	);

	return (
		<div className="flex flex-col gap-2 p-4">
			<div className="flex flex-wrap items-center gap-x-4 gap-y-2">
				<div className="flex flex-none flex-row items-center gap-1">
					<Button
						type="text"
						size="small"
						icon={<LeftOutlined className="!text-character-title-85" />}
						onClick={handlePrev}
					/>
					<span className="h5medium whitespace-nowrap text-character-primary">
						{currentViewLabel}
					</span>
					<Button
						type="text"
						size="small"
						icon={<RightOutlined className="!text-character-title-85" />}
						onClick={handleNext}
					/>
				</div>
				{leftExtra ? <div className="flex flex-none flex-row items-center">{leftExtra}</div> : null}
				{compact ? null : legend}
				<div className="ml-auto flex flex-none flex-row items-center gap-2">
					<Dropdown overlay={viewMenu} trigger={['click']}>
						<Button className="flex items-center gap-1">
							{view.charAt(0).toUpperCase() + view.slice(1)}
							<DownOutlined className="text-xs" />
						</Button>
					</Dropdown>
					<Button onClick={handleToday}>Today</Button>
					<span
						className="inline-flex items-baseline"
						style={{ backgroundColor: '#F0F0F0', padding: '5px 12px', borderRadius: 2 }}
					>
						{myTimeZoneAbbreviation}
					</span>
				</div>
			</div>
			{compact ? legend : null}
		</div>
	);
};

export default CustomCalendarToolbar;
